import { Actions as MasterProfileActions } from "@/store/masterProfile/slice";
import { Selectors as MasterProfileSelectors } from "@/store/masterProfile/selectors";
import { TaskEditorCacheService } from "../taskEditor/cache";
import { MasterServicesCacheService } from "./cache";
import { Selectors as SaloonProfileSelectors } from "@/store/saloonProfile/selectors";
import { Actions as SaloonProfileActions } from "@/store/saloonProfile/slice";
import { Selectors as UserSelectors } from "@/store/user/selectors";
import { Actions as UserActions } from "@/store/user/slice";
import { getReduxStore } from "@/store";
import type { tMasterService } from "@/types";
import { SaloonStaffCacheService } from "../saloonStaffService/cache";

export const MasterServiceMutationHandler = {
  serviceCreated({
    service,
    saloon_id,
  }: {
    service: tMasterService;
    saloon_id?: string;
  }) {
    const store = getReduxStore()!;
    if (shouldUpdateMasterProfilePage(service)) {
      store.dispatch(MasterProfileActions.createService(service));
    }
    if (shouldUpdateSaloonProfilePage(service)) {
      store.dispatch(SaloonProfileActions.createService(service));
    }
    if (shouldUpdateLoggedInUser(service)) {
      store.dispatch(UserActions.addMasterService(service));
    }

    if (saloon_id) {
      SaloonStaffCacheService.addMasterService({
        service,
        saloonId: saloon_id,
      });
    }
    MasterServicesCacheService.addService({ service });
    TaskEditorCacheService.addService({ service });
  },
  serviceUpdated({
    updated_service: service,
    saloon_id,
  }: {
    updated_service: tMasterService;
    saloon_id?: string;
  }) {
    const store = getReduxStore()!;
    if (shouldUpdateMasterProfilePage(service)) {
      store.dispatch(MasterProfileActions.updateService(service));
    }
    if (shouldUpdateSaloonProfilePage(service)) {
      store.dispatch(SaloonProfileActions.updateService(service));
    }
    if (shouldUpdateLoggedInUser(service)) {
      store.dispatch(UserActions.replaceMasterService(service));
    }

    if (saloon_id) {
      SaloonStaffCacheService.updateMasterService({
        service,
        saloonId: saloon_id,
      });
    }
    MasterServicesCacheService.replaceService({ service });
    TaskEditorCacheService.replaceService({ service });
  },
  serviceRemoved({
    service,
    saloon_id,
  }: {
    service: tMasterService;
    saloon_id?: string;
  }) {
    const store = getReduxStore()!;
    if (shouldUpdateMasterProfilePage(service)) {
      store.dispatch(MasterProfileActions.deleteService(service._id));
    }
    if (shouldUpdateSaloonProfilePage(service)) {
      store.dispatch(SaloonProfileActions.deleteService(service._id));
    }
    if (shouldUpdateLoggedInUser(service)) {
      store.dispatch(UserActions.removeMasterService(service._id));
    }

    if (saloon_id) {
      SaloonStaffCacheService.removeMasterService({
        service,
        saloonId: saloon_id,
      });
    }
    MasterServicesCacheService.removeService({ service });
    TaskEditorCacheService.removeService({ service });
  },
  servicePositionChanged({
    master_id,
    services_ids,
  }: {
    master_id: string;
    services_ids: string[];
  }) {
    if (shouldUpdateMasterProfilePage({ master: master_id })) {
      const store = getReduxStore()!;
      store.dispatch(MasterProfileActions.changeServicesOrder(services_ids));
    }
  },
};

const shouldUpdateMasterProfilePage = (
  service: Pick<tMasterService, "master">,
) => {
  const store = getReduxStore()!;
  return (
    MasterProfileSelectors.getMasterId(store.getState()) === service.master
  );
};

const shouldUpdateSaloonProfilePage = (service: tMasterService) => {
  const store = getReduxStore()!;
  const state = store.getState();
  return (
    SaloonProfileSelectors.getId(state) &&
    SaloonProfileSelectors.getMasters(state).find(
      ({ _id }: { _id: string }) => _id === service.master,
    )
  );
};
const shouldUpdateLoggedInUser = (service: Pick<tMasterService, "master">) => {
  const store = getReduxStore()!;
  const state = store.getState();
  return UserSelectors.getMasterId(state) === service.master;
};
